import { useDeferredValue, useState } from "react";
import { keepPreviousData, useQuery } from "@tanstack/react-query";
import { Link, useParams } from "react-router-dom";
import { Chargement, EnTetePage, Message, Vide } from "@cabinet/charte";
import { dossiersApi, ecrituresApi, ErreurApi } from "../api/client";
import { SelecteurDExercice } from "../composants/Exercices";
import { useExerciceCourant } from "../composants/exerciceCourant";
import { FicheDePiece } from "../composants/Piece";
import { LiensDExport } from "../composants/Exports";
import { Pagination, TableDuGrandLivre } from "../composants/TableDuGrandLivre";
import { Tiroir } from "../composants/Tiroir";
import { formaterNombre } from "../domaine/formats";

/** Mouvements par page : assez pour lire un mois de banque d'un coup d'œil. */
const TAILLE_DE_PAGE = 500;

/**
 * Grand livre d'un exercice : chaque compte mouvementé, ses mouvements dans
 * l'ordre, et le solde après chacun.
 *
 * Le filtre par compte accepte un préfixe : « 4411 » couvre le collectif et
 * tous ses sous-comptes ; avec un tiers, on lit le grand livre auxiliaire d'un
 * fournisseur ou d'un client. Le livre se lit par pages (ENF-03) : un compte
 * coupé par une page reprend son solde en tête de la suivante. Un mouvement
 * ouvre sa pièce dans un tiroir, sans quitter la page.
 */
export function PageGrandLivre() {
  const { id = "" } = useParams();
  const [compte, setCompte] = useState("");
  const [tiers, setTiers] = useState("");
  const [brouillard, setBrouillard] = useState(true);
  const [page, setPage] = useState(0);
  const [piece, setPiece] = useState<{ id: string; compte: string } | null>(null);
  const compteDiffere = useDeferredValue(compte);
  const tiersDiffere = useDeferredValue(tiers);

  const dossier = useQuery({ queryKey: ["dossier", id], queryFn: () => dossiersApi.consulter(id) });
  const { exercices, exercice, choisir } = useExerciceCourant(id);

  const grandLivre = useQuery({
    queryKey: ["grand-livre", exercice?.id, compteDiffere, tiersDiffere, brouillard, page],
    queryFn: () =>
      ecrituresApi.grandLivre(exercice!.id, {
        compte: compteDiffere.trim() || undefined,
        tiers: tiersDiffere.trim() || undefined,
        brouillard,
        page,
        taille: TAILLE_DE_PAGE,
      }),
    enabled: exercice != null,
    // La page précédente reste affichée pendant que la suivante arrive : la
    // lecture ne saute pas à chaque changement de page.
    placeholderData: keepPreviousData,
  });

  // Changer de filtre ramène à la première page : la page 7 d'un autre
  // filtre n'a aucun rapport avec celle-ci.
  const filtrer = <T,>(poser: (valeur: T) => void) => (valeur: T) => {
    poser(valeur);
    setPage(0);
  };

  const erreur = dossier.error ?? grandLivre.error;
  const livre = grandLivre.data;

  return (
    <>
      <EnTetePage
        surTitre={dossier.data?.raisonSociale ?? "Dossier"}
        titre="Grand livre"
        description="Le solde cumulé se lit après chaque mouvement, positif au débit, négatif au crédit. Les brouillards y figurent par défaut : les écarter donne la position arrêtée sur les seules pièces validées. Un mouvement s'ouvre sur sa pièce."
        actions={exercice != null ? <LiensDExport exercice={exercice.id} formats={["grand-livre", "fec"]} /> : undefined}
      />

      <div className="filtres-plan">
        <SelecteurDExercice exercices={exercices} exercice={exercice} surChoix={filtrer(choisir)} />
        <input
          type="search"
          value={compte}
          onChange={(e) => filtrer(setCompte)(e.target.value)}
          placeholder="Compte ou préfixe — 4411, 61…"
          aria-label="Compte"
        />
        <input
          type="search"
          value={tiers}
          onChange={(e) => filtrer(setTiers)(e.target.value)}
          placeholder="Tiers — ATLAS"
          aria-label="Tiers"
        />
        <label className="discret-texte">
          <input type="checkbox" checked={brouillard} onChange={(e) => filtrer(setBrouillard)(e.target.checked)} />{" "}
          Brouillards inclus
        </label>
        {livre != null && (
          <span className="decompte">
            {formaterNombre(livre.nombreDeMouvements)} mouvements · {livre.devise}
          </span>
        )}
      </div>

      {erreur != null && (
        <Message type="erreur" titre={erreur instanceof ErreurApi ? erreur.titre : "Erreur"}>
          <p style={{ margin: "6px 0 0" }}>{erreur.message}</p>
        </Message>
      )}

      {exercice != null && grandLivre.isPending && <Chargement quoi="le grand livre" />}

      {livre != null && livre.comptes.length === 0 && <Vide>Aucun mouvement sur cet exercice avec ces filtres.</Vide>}

      {livre != null && (
        <>
          <Pagination page={livre.page} nombreDePages={livre.nombreDePages} surPage={setPage} />
          <TableDuGrandLivre
            livre={livre}
            surMouvement={(mouvement, numero) => setPiece({ id: mouvement.piece, compte: numero })}
            entete={(numero) => (
              <Link
                className="discret-texte lien-lettrage"
                to={`/dossiers/${id}/lettrage?compte=${encodeURIComponent(numero)}${
                  tiersDiffere.trim() !== "" ? `&tiers=${encodeURIComponent(tiersDiffere.trim())}` : ""
                }`}
              >
                Lettrage
              </Link>
            )}
          />
          <Pagination page={livre.page} nombreDePages={livre.nombreDePages} surPage={setPage} />
        </>
      )}

      {piece != null && (
        <Tiroir titre="Pièce" surTitre={`Depuis le compte ${piece.compte}`} surFermeture={() => setPiece(null)}>
          <FicheDePiece piece={piece.id} dossier={id} compteEnEvidence={piece.compte} />
        </Tiroir>
      )}
    </>
  );
}
